import React, { useMemo, useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  normalizeToISODate,
  parseISODate,
  formatDateToISO,
  getTodayISODate,
  addDaysToISODate,
  getUpcomingWeekendISODate
} from "../../../utils/dateUtils";

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const DEFAULT_QUICK_ACTIONS = [
  { id: "today", label: "Today", resolver: () => getTodayISODate() },
  {
    id: "tomorrow",
    label: "Tomorrow",
    resolver: () => addDaysToISODate(getTodayISODate(), 1)
  },
  {
    id: "weekend",
    label: "This Weekend",
    resolver: () => getUpcomingWeekendISODate()
  }
];

const DatePickerBase = ({
  value,
  onChange,
  onClose,
  quickActions = DEFAULT_QUICK_ACTIONS,
  closeOnSelect = false
}) => {
  const normalizedValue = normalizeToISODate(value);
  const selectedDate = useMemo(() => parseISODate(normalizedValue), [normalizedValue]);
  const todayISO = getTodayISODate();
  const [currentMonth, setCurrentMonth] = useState(selectedDate || parseISODate(todayISO) || new Date());
  const lastSyncedValueRef = useRef(normalizedValue);

  useEffect(() => {
    if (normalizedValue === lastSyncedValueRef.current) {
      return;
    }

    lastSyncedValueRef.current = normalizedValue;

    if (selectedDate) {
      setCurrentMonth(selectedDate);
    }
  }, [normalizedValue, selectedDate]);

  const calendarDays = useMemo(() => {
    const base = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const firstDay = base.getDay();
    const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();

    const days = [];

    for (let i = 0; i < firstDay; i += 1) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
      const iso = formatDateToISO(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day, 12, 0, 0, 0));
      days.push({ day, iso });
    }

    return days;
  }, [currentMonth]);

  const handleDateChange = (isoDate) => {
    if (!isoDate) return;
    onChange?.(isoDate);
    if (closeOnSelect) {
      onClose?.();
    }
  };

  const handleQuickAction = (action) => {
    const nextISO = action.resolver();
    handleDateChange(nextISO);
    const parsed = parseISODate(nextISO);
    if (parsed) {
      setCurrentMonth(parsed);
    }
  };

  const handleMonthChange = (direction) => {
    setCurrentMonth((prev) => {
      const next = new Date(prev);
      next.setMonth(prev.getMonth() + direction);
      return next;
    });
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2 px-6 py-4 border-b border-white/10">
        {quickActions.map((action) => (
          <button
            key={action.id}
            type="button"
            onClick={() => handleQuickAction(action)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors border ${
              action.id === "today"
                ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/30"
                : "bg-white/10 text-white/70 border-white/20 hover:bg-white/20"
            }`}
          >
            {action.label}
          </button>
        ))}
      </div>

      <div className="px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={() => handleMonthChange(-1)}
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
          >
            <ChevronLeft size={20} className="text-white/70" />
          </button>
          <h4 className="text-white font-medium">
            {currentMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
          </h4>
          <button
            type="button"
            onClick={() => handleMonthChange(1)}
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
          >
            <ChevronRight size={20} className="text-white/70" />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1 mb-2">
          {WEEKDAY_LABELS.map((day) => (
            <div key={day} className="text-center text-xs text-white/50 font-medium py-2">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {calendarDays.map((dayItem, index) => {
            if (!dayItem) {
              return <div key={`empty-${index}`} className="invisible" />;
            }

            const isSelected = dayItem.iso === normalizedValue;
            const isToday = dayItem.iso === todayISO;

            return (
              <button
                key={dayItem.iso}
                type="button"
                onClick={() => handleDateChange(dayItem.iso)}
                className={`
                  aspect-square flex items-center justify-center rounded-lg
                  text-sm font-medium transition-all
                  ${isSelected
                    ? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/20"
                    : isToday
                      ? "bg-violet-500/20 text-violet-400 border border-violet-500/30"
                      : "text-white/70 hover:bg-white/10"
                  }
                `}
              >
                {dayItem.day}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DatePickerBase;
